import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import {isCaptionTagsHighlightingEnabled} from '@Libraries/social-media-params.library';
import type {HighlightedCaptionTextareaProps} from './highlighted-caption-textarea.types';
import styles from '../../social-post-caption.module.scss';

const escapeHTMLCharactersFromCaption = (caption: string): string => {
  let updatedCaption = caption.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  if (updatedCaption.endsWith('\n')) {
    const htmlNewLineChar = '<br/>';
    updatedCaption += htmlNewLineChar;
  }
  return updatedCaption;
};

const processCaptionToHighlightTags = (caption: string, accountType: HighlightedCaptionTextareaProps['accountType'], placeholder: string): string => {
  if (caption === '') {
    return `<span class="${styles.placeHolderText}">${placeholder}</span>`;
  }

  if (!isCaptionTagsHighlightingEnabled(accountType)) {
    return caption;
  }

  return caption.replace(/([@#][^\s,!?:&<>;"#']+)/g, (extractedWord) => {
    return `<span class="${styles.highlightTags}">${extractedWord}</span>`;
  });
};

export function HighlightedCaptionTextarea({
  value,
  onChange,
  onPaste,
  onClick,
  placeholder,
  accountType,
  isCountErrorVisible,
  textAreaRef,
}: HighlightedCaptionTextareaProps): ReactElement {
  const textAreaOverlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = (): void => {
      if (textAreaOverlayRef.current && textAreaRef.current) {
        textAreaOverlayRef.current.scrollTop = textAreaRef.current.scrollTop;
      }
    };

    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.addEventListener('scroll', handleScroll, {passive: true});
    }

    return (): void => {
      if (textArea) {
        textArea.removeEventListener('scroll', handleScroll);
      }
    };
  }, [textAreaRef.current, textAreaOverlayRef.current]);

  const currentCaptionAsHtml = useMemo((): string => {
    const captionWithEscapedChars = escapeHTMLCharactersFromCaption(value);
    return processCaptionToHighlightTags(captionWithEscapedChars, accountType, placeholder);
  }, [value, accountType, placeholder]);

  return (
    <div className={styles.textAreaWrapper}>
      <div
        ref={textAreaOverlayRef}
        className={`${styles.textArea} ${styles.textAreaOverlay} ${isCountErrorVisible ? styles.captionError : ''}`}
        contentEditable={false}
        aria-hidden="true"
        dangerouslySetInnerHTML={{__html: currentCaptionAsHtml}}
      />
      <textarea
        onPaste={onPaste}
        onClick={onClick}
        ref={textAreaRef}
        className={`${styles.textArea} ${isCountErrorVisible ? styles.captionError : ''}`}
        placeholder={placeholder}
        onChange={onChange}
        value={value}
      />
    </div>
  );
}
